"use client";

import { useEffect, useRef, useState, ReactNode } from "react";
import { GalleryContainer } from "@/containers/GalleryContainer/GalleryContainer";
import AvatarCard from "./AvatarCard";
import { groupBy, sortPhotos } from "@/utils/GalleryFunctions";
import { smartSearch } from "@/services/GalleriesService";
import {
  EthnicyType,
  GalleryI,
  SexType,
} from "@/interfaces/galleries/Galleries";
import { SearchResultsSummary } from "./SearchResultsSummary";
import Link from "next/link";
import { getDateFormat1 } from "@/utils/DateFormats";
import { GalleryContext } from "@/context/GalleryContext";

function LocalGalleryProvider({
  gallery,
  years,
  photos,
  children,
}: {
  gallery: GalleryI;
  years: string[];
  photos: { [key: string]: any[] };
  children: ReactNode;
}) {
  const [activeXIndex, setActiveXIndex] = useState(0);
  const [activeYIndex, setActiveYIndex] = useState(0);
  const value = {
    gallery,
    photos,
    years,
    activeXIndex,
    activeYIndex,
    setActiveXIndex,
    setActiveYIndex,
    getGallery: () => {},
    galleryLoader: false,
    setIsQuizAnswered: () => {},
  } as any;
  return (
    <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>
  );
}

interface InitialSearchParams {
  ethnicities: EthnicyType[] | undefined;
  categories: string[] | undefined;
  sexData: SexType[] | undefined;
  yearRange: { start?: number; end?: number } | undefined;
  targetYears: { start?: number; end?: number } | undefined;
}

interface GalleryListProps {
  initialSearchParams: InitialSearchParams;
}

export function GalleryList({ initialSearchParams }: GalleryListProps) {
  const { ethnicities, categories, sexData, yearRange, targetYears } =
    initialSearchParams;

  const [galleries, setGalleries] = useState<Record<string, GalleryI[]> | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [totalGalleries, setTotalGalleries] = useState(0);
  const wasLoading = useRef(false);

  const groupData = (data: GalleryI[]) => {
    const categoryMap: Record<string, GalleryI[]> = {};
    const used = new Set<string>();
    const sortedCategories = categories
      ? [...categories].sort((a, b) =>
          a.localeCompare(b, "en", { sensitivity: "base" }),
        )
      : [];
    sortedCategories?.forEach((category) => {
      const items = data.filter(
        (gallery) =>
          gallery.categories.includes(category) && !used.has(gallery._id),
      );
      categoryMap[category] = items;
      items.forEach((g) => used.add(g._id));
    });
    setTotalGalleries(used.size);
    setGalleries(categoryMap);
  };
  useEffect(() => {
    const fetchGalleries = async () => {
      setLoading(true);

      const body = {
        ethnicity: ethnicities as EthnicyType[],
        categories,
        sex: sexData as SexType[],
        ...(yearRange ? { birthdate: yearRange } : {}),
        ...(targetYears ? { targetYears } : {}),
      };
      const response = await smartSearch(body);
      if ("error" in response) {
        console.error(response.error);
        setLoading(false);
        return;
      }
      const orderedGalleries = [...response.data].sort((a, b) =>
        a.name.localeCompare(b.name, "en", { sensitivity: "base" }),
      );
      groupData(orderedGalleries);
      setLoading(false);
    };

    fetchGalleries();
  }, [
    ethnicities,
    categories,
    sexData,
    yearRange?.start,
    yearRange?.end,
    targetYears?.start,
    targetYears?.end,
  ]);

  useEffect(() => {
    if (loading) {
      wasLoading.current = true;
    } else if (wasLoading.current) {
      wasLoading.current = false;
      document
        .getElementById("search-btn")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading]);

  const properCase = (value: string) =>
    value
      .toLowerCase()
      .split(" ")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");

  return (
    <div className="flex relative flex-col w-full overflow-visible">
      {loading && (
        <div className="flex justify-center py-10">
          <h1>Loading galleries...</h1>
        </div>
      )}

      {!loading && galleries && Object.keys(galleries).length === 0 && (
        <h1>No galleries found.</h1>
      )}

      {!loading &&
        galleries &&
        Object.keys(galleries).length > 0 &&
        Object.keys(galleries).map((categoryName) => (
          <div key={`${categoryName}`}>
            {galleries[categoryName].map((gallery) => {
              const sortedPhotos = [...sortPhotos(gallery.photoGallery)];
              const fixedPhotos = sortedPhotos.filter(
                (photo) => photo.year.length === 4,
              );
              const photos = groupBy(fixedPhotos, "year");
              const years = Object.keys(photos);
              return (
                <div
                  key={gallery._id}
                  id={`gallery-${gallery._id}`}
                  className="flex flex-col gap-16 items-start scroll-mt-24 font-bold p-[2%]"
                >
                  <h2 className="font-bold">{gallery.name}</h2>

                  <div className="flex flex-col gap-2 py-2 md:flex-row md:items-baseline md:gap-4">
                    <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:gap-4">
                      <h4 className="text-gray-400 font-light">
                        {gallery.photoGallery.length} available pictures
                      </h4>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {gallery.categories.map((category: string, i: number) => (
                        <Link
                          href={`/categories/${category}`}
                          key={`category-tag-${i}`}
                          role="button"
                          className="category-tag font-extralight"
                        >
                          {category}
                        </Link>
                      ))}
                      {gallery.sex && (
                        <span
                          key={`sex-tag`}
                          role="button"
                          className="category-tag font-extralight"
                        >
                          {properCase(gallery.sex)}
                        </span>
                      )}
                      {gallery.ethnicity?.map((eth, idx) => (
                        <span
                          key={`ethnicity-tag-${idx}`}
                          role="button"
                          className="category-tag font-extralight"
                        >
                          {properCase(eth)}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="c-row flex-column flex-nowrap align-items-start">
                    <div className="c-row">
                      Date of birth: {getDateFormat1(gallery.dateStart)}
                    </div>
                    {gallery.dateEnd && (
                      <div className="c-row">
                        <h2 className="font-bold">
                          Date of death: {getDateFormat1(gallery.dateEnd)}
                        </h2>
                      </div>
                    )}
                  </div>

                  <div className="flex w-full gap-4">
                    <div className="md:w-full w-1/2 md:max-w-[200px] max-w-[200px]">
                      <AvatarCard gallery={gallery} />
                    </div>

                    <div className="w-full flex overflow-hidden">
                      <LocalGalleryProvider
                        gallery={gallery}
                        years={years}
                        photos={photos}
                      >
                        <GalleryContainer
                          years={years}
                          photos={photos}
                          gallery={gallery}
                        />
                      </LocalGalleryProvider>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

      {!loading && galleries && Object.keys(galleries).length > 0 && (
        <div
          className="sticky bottom-32 right-32 z-50"
          style={{ marginLeft: "auto" }}
        >
          <SearchResultsSummary
            galleries={galleries}
            totalGalleries={totalGalleries}
          />
        </div>
      )}
    </div>
  );
}
